<template>
  <div class="item-form-page p-5">
    <section class="page-header fade-in">
      <div>
        <h1 class="page-title">Modifier l'article</h1>
        <p class="page-subtitle">Mise à jour des informations de l'article.</p>
      </div>
    </section>

    <section class="fade-in" v-if="!initialLoading">
      <form class="" @submit.prevent="handleSubmit">
        <div class="">
          <div class="form-row">
            <div class="form-group">
              <label> Designation *</label>
              <input
                v-model.trim="form.fullName"
                type="text"
                class="form-control"
                :class="{ 'has-error': errors.fullName }"
              />
              <p v-if="errors.fullName" class="error-text">
                {{ errors.fullName }}
              </p>
            </div>

            <div class="form-group">
              <label> Reference </label>
              <input
                v-model.trim="form.code"
                type="text"
                class="form-control"
              />
            </div>
          </div>

          <!-- <div class="form-group">
            <label>Description</label>
            <textarea
              v-model.trim="form.description"
              rows="3"
              class="form-control"
              placeholder="Description de l'article"
            ></textarea>
          </div> -->

          <div class="form-row">
            <div class="form-group">
              <label>Prix TTC</label>
              <input
                v-model.number="form.priceTTC"
                type="number"
                step="0.01"
                min="0"
                class="form-control"
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Code-barres</label>
              <div class="input-with-button">
                <input
                  v-model.trim="form.barCode"
                  type="text"
                  class="form-control"
                />
                <button
                  type="button"
                  class="btn-scan"
                  @click="openScanner"
                  title="Scanner le code-barres"
                >
                  <i class="bi bi-upc-scan"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="form-actions">
          <button
            type="button"
            class="btn btn-light"
            @click="goBack"
            :disabled="submitting"
          >
            Annuler
          </button>
          <button type="submit" class="btn btn-primary" :disabled="submitting">
            <i class="bi bi-check-circle"></i>
            <span v-if="!submitting">Mettre à jour</span>
            <span v-else>Mise à jour...</span>
          </button>
        </div>
      </form>
    </section>

    <div v-else class="loading-state">
      <div class="spinner"></div>
      <p>Chargement de l'article...</p>
    </div>

    <!-- Barcode Scanner Modal -->
    <BarcodeScannerModal
      :show="showScanner"
      :use-api="true"
      api-url="/api/items/read-barcode"
      @scanned="onBarcodeScanned"
      @close="closeScanner"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import BarcodeScannerModal from "@/components/BarcodeScannerModal.vue";

export default {
  components: {
    BarcodeScannerModal,
  },
  name: "ItemEdit",
  data() {
    return {
      initialLoading: true,
      submitting: false,
      errors: {},
      showScanner: false,
      form: {
        reference: "",
        fullName: "",
        code: "",
        barCode: "",
        description: "",
        priceTTC: null,
      },
    };
  },
  computed: {
    ...mapGetters("item", {
      item: "getItem",
    }),
    itemReference() {
      return this.$route.params.reference;
    },
  },
  watch: {
    item: {
      handler(newValue) {
        if (!newValue || Object.keys(newValue).length === 0) {
          if (this.initialLoading) {
            this.initialLoading = false;
          }
          return;
        }
        this.populateForm(newValue);
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    goBack() {
      this.$router.back();
    },
    openScanner() {
      this.showScanner = true;
    },
    closeScanner() {
      this.showScanner = false;
    },
    onBarcodeScanned(barcode) {
      console.log("Barcode received in ItemEdit:", barcode);
      this.form.barCode = barcode;
      this.closeScanner(); // Close the scanner modal
      this.$store.dispatch("notification/store", {
        status: "success",
        message: "Code-barres scanné avec succès.",
      });
    },
    populateForm(itemData) {
      const mapped = this.mapItemToForm(itemData);
      Object.keys(mapped).forEach((key) => {
        this.$set(this.form, key, mapped[key]);
      });
      this.initialLoading = false;
    },
    mapItemToForm(item) {
      const toString = (value) => {
        return value === null || value === undefined ? "" : String(value);
      };

      return {
        reference: toString(item?.reference || this.itemReference),
        fullName: toString(item?.fullName || item?.name),
        code: toString(item?.code),
        barCode: toString(item?.barCode),
        description: toString(item?.description),
        priceTTC: item?.priceTTC || null,
      };
    },
    validate() {
      const errors = {};

      if (!this.form.fullName || !this.form.fullName.trim()) {
        errors.fullName = "Le nom de l'article est requis.";
      }

      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    buildPayload() {
      const payload = {
        fullName: this.form.fullName.trim(),
        code: this.form.code.trim() || null,
        barCode: this.form.barCode.trim() || null,
        description: this.form.description.trim() || null,
        priceTTC: this.form.priceTTC || null,
      };

      return payload;
    },
    async loadItem() {
      this.initialLoading = true;
      try {
        const result = await this.$store.dispatch(
          "item/show",
          this.itemReference
        );
        await this.$nextTick();
        await new Promise((resolve) => setTimeout(resolve, 100));

        if (this.item && Object.keys(this.item).length > 0) {
          this.populateForm(this.item);
        } else if (result && Object.keys(result).length > 0) {
          this.populateForm(result);
        } else {
          this.initialLoading = false;
        }
      } catch (error) {
        console.error("Error loading item", error);
        this.$store.dispatch("notification/store", {
          status: "error",
          message: "Impossible de charger l'article.",
        });
        this.initialLoading = false;
      }
    },
    async handleSubmit() {
      if (!this.validate()) {
        this.$toast?.error?.("Veuillez corriger les erreurs du formulaire");
        return;
      }

      this.submitting = true;
      try {
        const payload = this.buildPayload();
        await this.$store.dispatch("item/update", {
          reference: this.itemReference,
          data: payload,
        });

        this.$store.dispatch("notification/store", {
          status: "success",
          message: "Article mis à jour avec succès.",
        });

        await new Promise((resolve) => setTimeout(resolve, 500));

        this.$router.push({
          name: "items-show",
          params: { reference: this.itemReference },
        });
      } catch (error) {
        console.error("Error updating item:", error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Une erreur est survenue lors de la mise à jour.";

        this.$store.dispatch("notification/store", {
          status: "error",
          message: errorMessage,
        });

        this.$toast?.error?.(errorMessage);
      } finally {
        this.submitting = false;
      }
    },
  },
  async mounted() {
    if (
      !this.item ||
      Object.keys(this.item).length === 0 ||
      this.item.reference !== this.itemReference
    ) {
      await this.loadItem();
    } else {
      this.populateForm(this.item);
    }
  },
  beforeDestroy() {
    this.$store.dispatch("item/reset");
  },
};
</script>

<style scoped>
.item-form-page {
  min-height: 100vh;
  background: var(--bg-secondary);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.page-header .btn {
  margin-bottom: 1rem;
}

.page-title {
  font-size: 2.25rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.page-subtitle {
  margin: 0;
  color: var(--text-secondary);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

label {
  font-weight: 600;
  color: var(--text-secondary);
}

input,
textarea {
  border-radius: var(--radius-md);
  border: 1px solid var(--border-color);
  padding: 0.75rem 1rem;
  font-size: 1rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

input:focus,
textarea:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(63, 81, 181, 0.1);
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.form-actions {
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
}

.has-error {
  border-color: var(--danger-color);
}

.error-text {
  color: var(--danger-color);
  font-size: 0.875rem;
  margin: 0;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 60vh;
  color: var(--text-secondary);
}

.spinner {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 4px solid var(--border-color);
  border-top-color: var(--primary-color);
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-light {
  background: #f8fafc;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.btn-light:hover:not(:disabled) {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-primary:hover:not(:disabled) {
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
}

@media (max-width: 1024px) {
  .form-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .item-form-page {
    padding: 1rem;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .form-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }
}

.input-with-button {
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
}

.input-with-button .form-control {
  flex: 1;
}

.btn-scan {
  padding: 0.75rem 1rem;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  flex-shrink: 0;
}

.btn-scan:hover {
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  transform: translateY(-1px);
}

.btn-scan i {
  font-size: 1.25rem;
}

@media (max-width: 480px) {
  .page-title {
    font-size: 1.75rem;
  }
}
</style>
